import { getBrand } from "@api/brand";
import { getStoresByUser } from "@api/store";
import { TBrand, TStore } from "@components/types";
import { formatAddress, getSafeUrl, sanityIoImageLoader } from "@core/utils";
import { ArrowRight, MapPin, Share2, Store, Verified } from "lucide-react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";

type PageProps = {
    initialBrand?: TBrand;
    initialStores?: TStore[];
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (context) => {
    const brandId = 2467;

    if (!(brandId > 0)) {
        return { notFound: true };
    }

    const initialBrand = await getBrand(brandId).catch((): TBrand => null);
    if (!initialBrand?.id) {
        return { notFound: true };
    }

    const initialStores: TStore[] = await getStoresByUser(initialBrand?.owner_id || initialBrand?.added_by_id).catch((): TStore[] => []);
    return {
        props: {
            initialBrand,
            initialStores: initialStores || [],
        },
    };
};

const getStoreImage = (store: TStore): string =>
    store?.image || store?.logo || "https://cdn.jhattse.com/public/consumer/placeholder/store.png";

type ShareButtonProps = {
    title: string;
    path: string;
    className?: string;
    iconColor?: string;
};

const ShareButton: React.FC<ShareButtonProps> = ({ title, path, className = "", iconColor }) => {
    const handleShare = async (): Promise<void> => {
        const url = typeof window !== "undefined" ? `${window.location.origin}${path}` : path;
        if (typeof navigator !== "undefined" && navigator.share) {
            await navigator.share({ title, url }).catch((): void => undefined);
            return;
        }
        if (typeof navigator !== "undefined" && navigator.clipboard) {
            await navigator.clipboard.writeText(url).catch((): void => undefined);
        }
    };

    return (
        <button
            type="button"
            onClick={handleShare}
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-solid border-white/20 bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25 md:h-10 md:w-10 ${className}`}
            aria-label={`Share ${title}`}
        >
            <Share2 size={17} style={iconColor ? { color: iconColor } : undefined} />
        </button>
    );
};

const BrandShopLanding: React.FC<PageProps> = ({ initialBrand, initialStores = [] }) => {
    const brand = initialBrand;
    const brandImage = brand?.image || brand?.logo || "https://cdn.jhattse.com/public/consumer/placeholder/brand.png";
    const brandLogo = brand?.logo || brand?.image || "https://cdn.jhattse.com/public/consumer/placeholder/brand.png";
    const brandDescription = brand?.about || brand?.description || "Explore stores and services from this business.";
    const [activeTab, setActiveTab] = useState<"stores" | "about">("stores");
    const brandPath = `/shop/brand/2467`;

    return (
        <>
            <Head>
                <title>{`${brand?.name} Stores`}</title>
                <meta name="description" content={brandDescription} />
                <meta property="og:title" content={`${brand?.name} Stores`} />
                <meta property="og:description" content={brandDescription} />
                <meta property="og:image" content={brandImage} />
                <link rel="canonical" href={`https://jhattse.com/shop/brand/${brand?.id}/${getSafeUrl(brand?.name)}`} />
                <link rel="icon" href={brandLogo} />
                <link rel="shortcut icon" href={brandLogo} />
                <link rel="apple-touch-icon" href={brandLogo} />
            </Head>

            <main className="flex min-h-screen w-full max-w-[100vw] justify-center overflow-x-hidden bg-neutral-100 font-manrope text-neutral-950 md:block md:bg-neutral-50">
                <div className="w-full max-w-full overflow-x-hidden bg-neutral-50 md:overflow-visible">
                <section className="relative w-full max-w-full overflow-hidden bg-neutral-950 md:min-h-[420px]">
                    <div className="relative h-52 w-full max-w-full overflow-hidden opacity-80 md:absolute md:inset-0 md:h-auto md:opacity-45">
                        <Image
                            loader={sanityIoImageLoader}
                            src={brandImage}
                            alt={brand?.name || "Brand"}
                            fill
                            sizes="100vw"
                            className="object-cover object-center"
                            priority
                        />
                    </div>
                    <div className="absolute inset-0 hidden bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-neutral-950/30 md:block" />

                    <div className="relative mx-auto box-border flex w-full max-w-[1120px] flex-col justify-end px-4 pb-6 pt-0 md:min-h-[420px] md:px-8 md:pb-10 md:pt-20">
                        <div className="-mt-10 flex flex-col gap-4 md:mt-0 md:max-w-3xl md:gap-5">
                            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-solid border-white/80 bg-white shadow-lg md:h-20 md:w-20 md:border-white/20">
                                <Image
                                    loader={sanityIoImageLoader}
                                    src={brandLogo}
                                    alt={`${brand?.name || "Brand"} logo`}
                                    width={96}
                                    height={96}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div>
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    {brand?.published && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                                            <Verified size={13} />
                                            Published
                                        </span>
                                    )}
                                    {brand?.homegrown && (
                                        <span className="rounded-full bg-green-500/90 px-3 py-1 text-xs font-bold text-white">
                                            Homegrown
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-start justify-between gap-3">
                                    <h1 className="min-w-0 text-2xl font-extrabold tracking-normal text-white md:text-5xl">
                                        {brand?.name}
                                    </h1>
                                    <ShareButton title={brand?.name || "Brand"} path={brandPath} />
                                </div>
                                <p className="mt-3 line-clamp-4 max-w-2xl text-xs leading-5 text-white/75 md:mt-4 md:line-clamp-none md:text-base md:leading-6 md:text-white/80">
                                    {brandDescription}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mx-auto box-border w-full max-w-[1120px] overflow-x-hidden px-4 py-6 md:px-8 md:py-10">
                    <div className="mb-6 grid grid-cols-2 rounded-2xl border border-solid border-neutral-100 bg-white p-1 shadow-sm">
                        <button
                            type="button"
                            onClick={() => setActiveTab("stores")}
                            className={`h-11 rounded-xl border-none text-sm font-extrabold transition-colors ${activeTab === "stores" ? "bg-neutral-950 text-white" : "bg-transparent text-neutral-500"}`}
                        >
                            Available Store
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("about")}
                            className={`h-11 rounded-xl border-none text-sm font-extrabold transition-colors ${activeTab === "about" ? "bg-neutral-950 text-white" : "bg-transparent text-neutral-500"}`}
                        >
                            About Us
                        </button>
                    </div>

                    {activeTab === "stores" ? (
                        <>
                            <div className="mb-5 flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">Available stores</p>
                                    <h2 className="mt-1 text-2xl font-extrabold text-neutral-950">Choose a store</h2>
                                </div>
                                <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-neutral-600 shadow-sm">
                                    {initialStores?.length || 0} store{initialStores?.length === 1 ? "" : "s"}
                                </span>
                            </div>

                            {initialStores?.length > 0 ? (
                                <div className="grid gap-4 md:grid-cols-2">
                                    {initialStores.map((store) => {
                                        const storePath = `/shop/${store?.id}/${getSafeUrl(store?.name)}`;
                                        return (
                                            <div
                                                key={store?.id}
                                                className="group relative overflow-hidden rounded-2xl border border-solid border-neutral-100 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                                            >
                                                <ShareButton
                                                    title={store?.name || "Store"}
                                                    path={storePath}
                                                    className="absolute right-2 top-2 z-10 h-8 w-8 border-neutral-200 bg-white text-neutral-700 shadow-sm hover:bg-neutral-50"
                                                    iconColor="#111827"
                                                />
                                                <div className="grid grid-cols-[88px_1fr] gap-3 p-3 sm:grid-cols-[112px_1fr] sm:gap-4 sm:p-4">
                                                    <Link href={storePath} className="relative h-24 overflow-hidden rounded-xl bg-neutral-100 sm:h-28" prefetch={false}>
                                                        <Image
                                                            loader={sanityIoImageLoader}
                                                            src={getStoreImage(store)}
                                                            alt={store?.name || "Store"}
                                                            fill
                                                            className="object-cover transition-transform duration-300 group-hover:scale-105"
                                                        />
                                                    </Link>

                                                    <div className="flex min-w-0 flex-col justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <Link href={storePath} className="min-w-0 cursor-pointer" prefetch={false}>
                                                                    <h3 className="line-clamp-2 text-base font-extrabold text-neutral-950 sm:text-lg">
                                                                        {store?.name}
                                                                    </h3>
                                                                </Link>
                                                                <div className="flex shrink-0 items-center gap-1">
                                                                    {store?.verified && <Verified size={17} className="text-green-600" />}
                                                                </div>
                                                            </div>
                                                            <p className="mt-2 line-clamp-2 text-xs leading-5 text-neutral-500 sm:text-sm">
                                                                {formatAddress(store?.address) || store?.address?.city?.name || "Store location"}
                                                            </p>
                                                        </div>

                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="inline-flex min-w-0 items-center gap-1 text-xs font-bold text-neutral-500">
                                                                <MapPin size={13} className="shrink-0" />
                                                                <span className="truncate">{store?.address?.city?.name || "Open store"}</span>
                                                            </span>
                                                            <Link href={storePath} className="inline-flex items-center gap-1 rounded-full bg-neutral-950 px-3 py-1.5 text-xs font-bold !text-white hover:!text-white focus:!text-white visited:!text-white" prefetch={false}>
                                                                Open
                                                                <ArrowRight size={13} />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center rounded-2xl border border-solid border-neutral-100 bg-white px-5 py-14 text-center shadow-sm">
                                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-neutral-100 text-neutral-500">
                                        <Store size={24} />
                                    </div>
                                    <h3 className="text-lg font-extrabold text-neutral-950">No stores available</h3>
                                    <p className="mt-2 max-w-md text-sm leading-6 text-neutral-500">
                                        Stores linked with this business will appear here once they are available.
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="rounded-2xl border border-solid border-neutral-100 bg-white p-5 shadow-sm md:p-7">
                            <h2 className="text-2xl font-extrabold text-neutral-950">About {brand?.name}</h2>
                            <p className="mt-4 text-sm leading-7 text-neutral-600 md:text-base">
                                {brandDescription}
                            </p>
                            {brand?.published_at && (
                                <p className="mt-5 text-xs font-bold uppercase tracking-wide text-neutral-400">
                                    Published on {new Date(brand.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                </p>
                            )}
                        </div>
                    )}
                </section>
                </div>
            </main>
        </>
    );
};

export default BrandShopLanding;
